/**
 * UserDetailPanel — Slide-out panel for viewing and editing a user.
 *
 * Admins can edit: department, manager (reports_to), role, title, status.
 * Regular users see read-only view.
 * Slide-in from the right with backdrop.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import User from 'lucide-react/dist/esm/icons/user';
import Building2 from 'lucide-react/dist/esm/icons/building-2';
import GitBranch from 'lucide-react/dist/esm/icons/git-branch';
import Shield from 'lucide-react/dist/esm/icons/shield';
import Briefcase from 'lucide-react/dist/esm/icons/briefcase';
import Mail from 'lucide-react/dist/esm/icons/mail';
import Check from 'lucide-react/dist/esm/icons/check';
import AlertTriangle from 'lucide-react/dist/esm/icons/alert-triangle';
import UserX from 'lucide-react/dist/esm/icons/user-x';
import KeyRound from 'lucide-react/dist/esm/icons/key-round';
import Puzzle from 'lucide-react/dist/esm/icons/puzzle';
import type { OrgUser, Department, ModuleRegistryItem, UserModuleData } from '../../../services/api/org';
import { getUserModules, setUserModules, setUserModuleRole } from '../../../services/api/org';
import { Button, IconButton, TextField, Text, SlidePanel, Switch } from '../../../components/ui';
import { Select } from '../../../components/ui';
import ModuleBadges from '../../../components/ui/ModuleBadges';
import ResetPasswordModal from './ResetPasswordModal';

interface UserDetailPanelProps {
  user: OrgUser | null;
  isOpen: boolean;
  onClose: () => void;
  isAdmin: boolean;
  users: OrgUser[];
  departments: Department[];
  onSave: (userId: string, data: Record<string, any>) => Promise<void>;
  onSetReporting: (userId: string, managerId: string | null) => Promise<void>;
  onDeactivate?: (userId: string) => Promise<void>;
  orgEnabledModules?: string[];
  moduleRegistry?: ModuleRegistryItem[];
}

const ROLES = [
  { value: 'user', label: 'User' },
  { value: 'org_admin', label: 'Org Admin' },
  { value: 'tenant_admin', label: 'Tenant Admin' },
];

const ROLE_COLORS: Record<string, string> = {
  platform_admin: 'text-status-error',
  tenant_admin: 'text-purple-400',
  org_admin: 'text-blue-400',
  user: 'text-content-tertiary',
};

const UserDetailPanel: React.FC<UserDetailPanelProps> = ({
  user,
  isOpen,
  onClose,
  isAdmin,
  users,
  departments,
  onSave,
  onSetReporting,
  onDeactivate,
  orgEnabledModules = [],
  moduleRegistry = [],
}) => {
  const [title, setTitle] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [departmentId, setDepartmentId] = useState<string>('');
  const [managerId, setManagerId] = useState<string>('');
  const [role, setRole] = useState('user');
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);

  const [userModuleAccess, setUserModuleAccess] = useState<string[]>([]);
  const [userModuleRoles, setUserModuleRoles] = useState<Record<string, string>>({});
  const [moduleDataLoaded, setModuleDataLoaded] = useState(false);

  // Reset when user changes
  useEffect(() => {
    if (user) {
      setTitle(user.title || '');
      setDisplayName(user.display_name || '');
      setDepartmentId(user.department_id || '');
      setManagerId(user.reports_to || '');
      setRole(user.system_role);
      setDirty(false);
      setSaving(false);
      setShowDeactivateConfirm(false);
      setShowResetPassword(false);
      setModuleDataLoaded(false);
    }
  }, [user]);

  // Load user module data when panel opens for an admin
  useEffect(() => {
    if (!user || !isAdmin || !isOpen || moduleDataLoaded) return;
    let cancelled = false;
    getUserModules(user.id)
      .then((data: UserModuleData) => {
        if (cancelled) return;
        setUserModuleAccess(data.user_modules);
        setUserModuleRoles(data.module_roles);
        setModuleDataLoaded(true);
      })
      .catch(() => {
        if (!cancelled) setModuleDataLoaded(true);
      });
    return () => { cancelled = true; };
  }, [user, isAdmin, isOpen, moduleDataLoaded]);

  const toggleModuleAccess = useCallback((moduleId: string) => {
    setUserModuleAccess((prev) =>
      prev.includes(moduleId) ? prev.filter((m) => m !== moduleId) : [...prev, moduleId],
    );
    setDirty(true);
  }, []);

  const handleModuleRoleChange = useCallback((moduleId: string, newRole: string) => {
    setUserModuleRoles((prev) => ({ ...prev, [moduleId]: newRole }));
    setDirty(true);
  }, []);

  const enabledRegistryItems = useMemo(
    () => moduleRegistry.filter((m) => orgEnabledModules.includes(m.id)),
    [moduleRegistry, orgEnabledModules],
  );

  // Potential managers: all active users except this user and their reports
  const managerOptions = useMemo(() => {
    if (!user) return [];
    return users.filter(
      (u) => u.id !== user.id && u.status !== 'deactivated'
    );
  }, [users, user]);

  const currentManager = useMemo(
    () => users.find((u) => u.id === user?.reports_to),
    [users, user]
  );

  const currentDept = useMemo(
    () => departments.find((d) => d.id === user?.department_id),
    [departments, user]
  );

  const handleFieldChange = () => {
    setDirty(true);
  };

  const handleSave = async () => {
    if (!user || !dirty) return;
    setSaving(true);
    try {
      const updates: Record<string, any> = {};
      if (displayName !== (user.display_name || '')) updates.display_name = displayName || null;
      if (title !== (user.title || '')) updates.title = title || null;
      if (departmentId !== (user.department_id || '')) updates.department_id = departmentId || null;
      if (role !== user.system_role) updates.system_role = role;

      if (Object.keys(updates).length > 0) {
        await onSave(user.id, updates);
      }

      if (managerId !== (user.reports_to || '')) {
        await onSetReporting(user.id, managerId || null);
      }

      // Persist module access changes
      if (isAdmin && enabledRegistryItems.length > 0) {
        const modulePayload = enabledRegistryItems.map((m) => ({
          module_id: m.id,
          enabled: userModuleAccess.includes(m.id),
        }));
        await setUserModules(user.id, modulePayload);

        // Persist per-module role changes
        for (const mod of enabledRegistryItems) {
          const newRole = userModuleRoles[mod.id];
          if (newRole && userModuleAccess.includes(mod.id)) {
            await setUserModuleRole(user.id, mod.id, newRole);
          }
        }
      }

      setDirty(false);
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  const initial = (user.display_name || user.email)[0]?.toUpperCase();
  const isUnassigned = !user.department_id && !user.reports_to;

  return (
    <SlidePanel isOpen={isOpen} onClose={onClose} width="max-w-[460px]">
      {/* Custom header with avatar */}
      <div className="flex items-center gap-4 px-6 py-5 border-b border-subtle">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-brand/30 to-status-success/30 flex items-center justify-center text-lg font-bold text-content-inverse shrink-0">
          {initial}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-semibold text-content truncate">
            {user.display_name || user.email.split('@')[0]}
          </h3>
          <div className="flex items-center gap-2 mt-0.5">
            <Mail size={11} className="text-content-faint shrink-0" />
            <span className="text-xs text-content-muted truncate">{user.email}</span>
          </div>
        </div>
      </div>

      {/* Status badges */}
      <div className="flex items-center gap-2 px-6 py-3 border-b border-subtle">
        <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium
          ${user.status === 'active' ? 'bg-emerald-400/10 text-emerald-400 border border-emerald-400/20' : ''}
          ${user.status === 'invited' ? 'bg-amber-400/10 text-amber-400 border border-amber-400/20' : ''}
          ${user.status === 'deactivated' ? 'bg-surface-primary text-content-muted border border-default' : ''}
        `}>
          {user.status === 'active' && <Check size={10} />}
          {user.status === 'invited' && <Mail size={10} />}
          {user.status}
        </span>
        {isUnassigned && (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-medium bg-amber-400/10 text-amber-400 border border-amber-400/20">
            <AlertTriangle size={10} />
            unassigned
          </span>
        )}
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
        {/* Display Name */}
        <FieldGroup label="Display Name" icon={<User size={13} className="text-content-muted" />}>
          {isAdmin ? (
            <TextField
              value={displayName}
              onChange={(e) => { setDisplayName(e.target.value); handleFieldChange(); }}
              placeholder="Full name"
            />
          ) : (
            <ReadOnlyValue value={user.display_name} />
          )}
        </FieldGroup>

        {/* Title */}
        <FieldGroup label="Job Title" icon={<Briefcase size={13} className="text-content-muted" />}>
          {isAdmin ? (
            <TextField
              value={title}
              onChange={(e) => { setTitle(e.target.value); handleFieldChange(); }}
              placeholder="e.g. VP of Operations, Software Engineer..."
            />
          ) : (
            <ReadOnlyValue value={user.title} />
          )}
        </FieldGroup>

        {/* Department */}
        <FieldGroup label="Department" icon={<Building2 size={13} className="text-content-muted" />}>
          {isAdmin ? (
            <Select
              value={departmentId}
              onChange={(val) => { setDepartmentId(val); handleFieldChange(); }}
              options={[
                { value: '', label: 'No department' },
                ...departments.map((d) => ({ value: d.id, label: d.name })),
              ]}
              placeholder="No department"
            />
          ) : (
            <ReadOnlyValue value={currentDept?.name} fallback="Not assigned" />
          )}
        </FieldGroup>

        {/* Reports To */}
        <FieldGroup label="Reports To" icon={<GitBranch size={13} className="text-content-muted" />}>
          {isAdmin ? (
            <Select
              value={managerId}
              onChange={(val) => { setManagerId(val); handleFieldChange(); }}
              options={[
                { value: '', label: 'No manager (top-level)' },
                ...managerOptions.map((u) => ({
                  value: u.id,
                  label: `${u.display_name || u.email.split('@')[0]}${u.title ? ` — ${u.title}` : ''}`,
                })),
              ]}
              placeholder="No manager (top-level)"
            />
          ) : (
            <ReadOnlyValue
              value={currentManager ? (currentManager.display_name || currentManager.email) : undefined}
              fallback="No manager assigned"
            />
          )}
        </FieldGroup>

        {/* System Role */}
        <FieldGroup label="System Role" icon={<Shield size={13} className="text-content-muted" />}>
          {isAdmin && user.system_role !== 'platform_admin' ? (
            <Select
              value={role}
              onChange={(val) => { setRole(val); handleFieldChange(); }}
              options={ROLES.map((r) => ({ value: r.value, label: r.label }))}
              placeholder="Select role"
            />
          ) : (
            <span className={`text-sm font-medium ${ROLE_COLORS[user.system_role] || 'text-content-tertiary'}`}>
              {user.system_role === 'platform_admin' ? 'Platform Admin' :
               ROLES.find((r) => r.value === user.system_role)?.label || user.system_role}
            </span>
          )}
        </FieldGroup>

        {/* Module Access (admin-only) */}
        {isAdmin && enabledRegistryItems.length > 0 && (
          <FieldGroup label="Module Access" icon={<Puzzle size={13} className="text-content-muted" />}>
            <div className="space-y-2">
              {enabledRegistryItems.map((mod) => (
                <div key={mod.id} className="flex items-center justify-between py-1">
                  <div className="flex items-center gap-2">
                    <ModuleBadges modules={[mod.id]} size="sm" />
                    <span className="text-sm text-content-secondary">{mod.name}</span>
                  </div>
                  <Switch
                    size="sm"
                    checked={userModuleAccess.includes(mod.id)}
                    onChange={() => toggleModuleAccess(mod.id)}
                  />
                </div>
              ))}
            </div>
          </FieldGroup>
        )}

        {/* Module Roles (admin-only, for modules user has access to) */}
        {isAdmin && enabledRegistryItems.some((m) => userModuleAccess.includes(m.id)) && (
          <FieldGroup label="Module Roles" icon={<Puzzle size={13} className="text-content-muted" />}>
            <div className="space-y-3">
              {enabledRegistryItems
                .filter((m) => userModuleAccess.includes(m.id))
                .map((mod) => (
                  <div key={mod.id}>
                    <div className="flex items-center gap-2 mb-1.5">
                      <ModuleBadges modules={[mod.id]} size="sm" />
                      <Text variant="caption" className="text-content-muted">{mod.name}</Text>
                    </div>
                    <Select
                      value={userModuleRoles[mod.id] || mod.default_role}
                      onChange={(val) => handleModuleRoleChange(mod.id, val)}
                      options={mod.roles.map((r) => ({ value: r.id, label: r.name }))}
                      placeholder="Select role"
                    />
                  </div>
                ))}
            </div>
          </FieldGroup>
        )}
      </div>

      {/* Footer */}
      {isAdmin && (
        <div className="px-6 py-4 border-t border-subtle flex items-center gap-3">
          <div className="flex items-center gap-1 mr-auto">
            {user.status !== 'deactivated' && user.system_role !== 'platform_admin' && (
              <IconButton
                icon={<KeyRound size={16} />}
                variant="ghost"
                aria-label="Reset password"
                onClick={() => setShowResetPassword(true)}
              />
            )}
            {onDeactivate && user.system_role !== 'platform_admin' && (
              <>
                {showDeactivateConfirm ? (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-status-error">Deactivate this user?</span>
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => { onDeactivate(user.id); onClose(); }}
                    >
                      Yes
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowDeactivateConfirm(false)}
                    >
                      No
                    </Button>
                  </div>
                ) : (
                  <IconButton
                    icon={<UserX size={16} />}
                    variant="danger"
                    aria-label="Deactivate user"
                    onClick={() => setShowDeactivateConfirm(true)}
                  />
                )}
              </>
            )}
          </div>

          <Button variant="ghost" size="lg" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="lg"
            loading={saving}
            disabled={!dirty}
            onClick={handleSave}
          >
            Save Changes
          </Button>
        </div>
      )}

      {showResetPassword && (
        <ResetPasswordModal
          isOpen={showResetPassword}
          user={user}
          onClose={() => setShowResetPassword(false)}
        />
      )}
    </SlidePanel>
  );
};

// ── Helpers ──

const FieldGroup: React.FC<{
  label: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}> = ({ label, icon, children }) => (
  <div>
    <Text variant="label" className="flex items-center gap-1.5 mb-2">
      {icon}
      {label}
    </Text>
    {children}
  </div>
);

const ReadOnlyValue: React.FC<{ value?: string | null; fallback?: string }> = ({
  value,
  fallback = 'Not set',
}) => (
  <p className={`text-sm ${value ? 'text-content' : 'text-content-faint italic'}`}>
    {value || fallback}
  </p>
);

export default React.memo(UserDetailPanel);
